/**
 * Entity/Tag Browser data hooks (frontend.md §5 step 13 — Tier-2 thin-first).
 *
 * The browser is thin-first (frontend.md §1.3): list + drill-down + filter-to-Search
 * handoff. These hooks cover exactly that surface area — entity/tag lists, the entity/tag
 * detail reads, co-occurrence + timeline for the detail panes. No rich co-occurrence
 * visualizations (deferred to v1.5).
 */

import { useInfiniteQuery, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getEntity,
  getEntityCooccurrences,
  getEntityTimeline,
  getRelatedTags,
  getTagTimeline,
  listEntities,
  listTags,
} from '@/api';
import type {
  EntityId,
  EntityList,
  EntityTypeName,
  EntityWithMetadata,
  Granularity,
  TagList,
  TimeSeries,
} from '@/types';
import { RENDER_DEFAULTS } from '@/config/renderDefaults';
import { useErrorHandler } from './useErrorHandler';

export function useEntityListQuery(params: {
  type?: EntityTypeName;
  types?: EntityTypeName[];
  prefix?: string;
  minCount?: number;
  sort?: 'name' | 'count';
}) {
  const { defaultPageSize } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  const query = useInfiniteQuery({
    queryKey: [
      'entities',
      params.type,
      params.types,
      params.prefix,
      params.minCount,
      params.sort,
      defaultPageSize,
    ],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<{ data: EntityList; nextCursor: string | null }> => {
      try {
        const r = await listEntities({
          type: params.type,
          types: params.types,
          prefix: params.prefix,
          minCount: params.minCount,
          sort: params.sort,
          limit: defaultPageSize,
          cursor: pageParam,
        });
        return { data: r.data, nextCursor: r.meta.nextCursor };
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = (query.data?.pages ?? []).flatMap((p) => p.data.items);
  return { query, items };
}

export function useTagListQuery(params: { prefix?: string; minCount?: number }) {
  const { defaultPageSize } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  const query = useInfiniteQuery({
    queryKey: ['tags', params.prefix, params.minCount, defaultPageSize],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<{ data: TagList; nextCursor: string | null }> => {
      try {
        const r = await listTags({
          prefix: params.prefix,
          minCount: params.minCount,
          limit: defaultPageSize,
          cursor: pageParam,
        });
        return { data: r.data, nextCursor: r.meta.nextCursor };
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = (query.data?.pages ?? []).flatMap((p) => p.data.items);
  return { query, items };
}

export function useEntityDetailQuery(
  entityId: EntityId | null,
): UseQueryResult<EntityWithMetadata, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['entity', entityId],
    enabled: entityId !== null,
    queryFn: async () => {
      try {
        return (await getEntity({ id: entityId as EntityId })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useEntityCooccurrencesQuery(
  entityId: EntityId | null,
): UseQueryResult<EntityList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['entity-cooccurrences', entityId],
    enabled: entityId !== null,
    queryFn: async () => {
      try {
        return (await getEntityCooccurrences({ id: entityId as EntityId })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useEntityTimelineQuery(
  entityId: EntityId | null,
  granularity: Granularity,
): UseQueryResult<TimeSeries, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['entity-timeline', entityId, granularity],
    enabled: entityId !== null,
    queryFn: async () => {
      try {
        return (await getEntityTimeline({ id: entityId as EntityId, granularity })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useRelatedTagsQuery(tag: string | null): UseQueryResult<TagList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['related-tags', tag],
    enabled: tag !== null,
    queryFn: async () => {
      try {
        return (await getRelatedTags({ tag: tag as string })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useTagTimelineQuery(
  tag: string | null,
  granularity: Granularity,
): UseQueryResult<TimeSeries, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['tag-timeline', tag, granularity],
    enabled: tag !== null,
    queryFn: async () => {
      try {
        return (await getTagTimeline({ tag: tag as string, granularity })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}
